import { Base, withBreakpointObserver, withMountWhenInView } from '@studiometa/js-toolkit';
import Matter from 'matter-js';

/**
 * CanvasBubbles component
 */
export default class CanvasBubbles extends withMountWhenInView(withBreakpointObserver(Base)) {
  /**
   * Component Configuration
   */
  static config = {
    name: 'CanvasBubbles',
    options: {
      activeBreakpoints: {
        type: String,
        default: 'l xl 2xl 3xl',
      },
      theme: {
        type: String,
        default: 'light', // light|dark|black
      },
      bubblesSpawns: {
        type: String,
        default: 'wide', // wide|sides
      },
    },
  };

  intersects = [];

  // Canvas
  canvas = null;

  width = 0;

  height = 0;

  pixelRatio = 1;

  // App config
  options = {
    gravity: {
      x: 0,
      y: -0.5,
    },
    backgroundColor: '#ffffff',
    bubbleStyle: {
      fillStyle: 'transparent',
      strokeStyle: '#e9e9e9',
      lineWidth: 1,
    },
  };

  // Matter
  engine = null;

  render = null;

  runner = null;

  mouse = null;

  isRunning = false;

  isAlreadyCreate = false;

  // Bodies
  groundBody = null;

  cursorBody = null;

  // Bubbles events
  bubbles = [];

  /**
   * mounted hook
   */
  mounted() {
    if (this.isAlreadyCreate) {
      this.launch();
      return;
    }

    if (this.$options.theme === 'dark') {
      this.options.backgroundColor = '#1a1a1a';
      this.options.bubbleStyle.strokeStyle = '#333333';
    } else if (this.$options.theme === 'black') {
      this.options.backgroundColor = '#000000';
      this.options.bubbleStyle.strokeStyle = '#333333';
    }

    // Canvas
    const canvas = document.createElement('canvas');
    this.$el.append(canvas);

    this.canvas = canvas;
    this.width = this.$el.parentNode.offsetWidth;
    this.height = this.$el.parentNode.offsetHeight;

    this.$el.style.width = `${this.width}px`;
    this.$el.style.height = `${this.height}px`;

    // Init Matter engine
    this.initEngine();

    // Init world bodies
    this.initWorld();

    // Init events
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onBeforeUpdate = this.onBeforeUpdate.bind(this);
    this.onCollisionStart = this.onCollisionStart.bind(this);
    this.onCollisionStartPairs = this.onCollisionStartPairs.bind(this);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    Matter.Events.on(this.engine, 'beforeUpdate', this.onBeforeUpdate);
    Matter.Events.on(this.engine, 'collisionStart', this.onCollisionStart);

    /* eslint-disable prettier/prettier */

    // Create bubbles
    if (this.$options.bubblesSpawns === 'wide') {
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.066, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.091, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.233, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.233, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.247, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.266, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.266, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.438, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.444, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.444, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.459, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.483, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.505, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.655, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.675, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.7, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.716, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.736, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.772, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.9, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.925, 1.1, 12);
    } else if (this.$options.bubblesSpawns === 'sides') {
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.077, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.097, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.111, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.122, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.141, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.859, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.878, 1.1, 8);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.889, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.903, 1.1, 12);
      this.addBubblePath(this.randomMinMax(400, 1000), this.randomMinMax(400, 1000), 0.923, 1.1, 8);
    }

    /* eslint-enable prettier/prettier */

    this.isAlreadyCreate = true;

    this.launch();
  }

  /**
   * destroyed hook
   */
  destroyed() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    Matter.Events.off(this.engine, 'beforeUpdate', this.onBeforeUpdate);
    Matter.Events.off(this.engine, 'collisionStart', this.onCollisionStart);

    Matter.Render.stop(this.render);
    Matter.World.clear(this.engine.world);
    Matter.Engine.clear(this.engine);
    this.render.canvas.remove();
    this.render.canvas = null;
    this.render.context = null;
    this.render.textures = {};

    this.bubbles.forEach((bubble) => {
      if (!bubble.event) {
        return;
      }
      clearInterval(bubble.event);
      bubble.event = null;
    });

    this.isAlreadyCreate = false;
    this.isRunning = false;
  }

  /**
   * resized hook
   */
  resized() {
    this.width = this.$el.parentNode.offsetWidth;
    this.height = this.$el.parentNode.offsetHeight;

    this.$el.style.width = `${this.width}px`;
    this.$el.style.height = `${this.height}px`;

    this.render.bounds.max.x = this.width;
    this.render.bounds.max.y = this.height;
    this.render.options.width = this.width;
    this.render.options.height = this.height;
    this.render.canvas.width = this.width;
    this.render.canvas.height = this.height;
  }

  /**
   * Launch
   */
  launch() {
    if (this.isRunning) {
      return;
    }

    // run the renderer
    Matter.Render.run(this.render);
    // create runner
    this.runner = Matter.Runner.create();
    // run the engine
    Matter.Runner.run(this.runner, this.engine);

    this.bubbles.forEach((bubble) => {
      bubble.event = setInterval(() => {
        this.addBubbleBody(
          bubble.config.x,
          bubble.config.y,
          bubble.config.size,
          bubble.config.style,
        );
      }, bubble.interval);
    });

    this.isRunning = true;
  }

  /**
   * Pause
   */
  pause() {
    if (!this.isRunning) {
      return;
    }

    Matter.Render.stop(this.render);

    this.bubbles.forEach((bubble) => {
      if (!bubble.event) {
        return;
      }
      clearInterval(bubble.event);
      bubble.event = null;
    });

    this.isRunning = false;
  }

  /**
   * Play
   */
  play() {
    if (this.isRunning) {
      return;
    }

    Matter.Render.run(this.render);

    this.bubbles.forEach((bubble) => {
      bubble.event = setInterval(() => {
        this.addBubbleBody(
          bubble.config.x,
          bubble.config.y,
          bubble.config.size,
          bubble.config.style,
        );
      }, bubble.interval);
    });

    this.isRunning = true;
  }

  /**
   * Init engine
   */
  initEngine() {
    // Create an engine
    this.engine = Matter.Engine.create();
    this.engine.gravity.x = this.options.gravity.x;
    this.engine.gravity.y = this.options.gravity.y;

    // Create a renderer
    this.render = Matter.Render.create({
      canvas: this.canvas,
      engine: this.engine,
      options: {
        width: this.width,
        height: this.height,
        pixelRatio: this.pixelRatio,
        background: this.options.backgroundColor,
        wireframes: false,
      },
    });

    // Create a mouse
    this.mouse = Matter.Mouse.create(this.render.canvas);
    this.render.mouse = this.mouse;
  }

  /**
   * Init world
   */
  initWorld() {
    // Create cursor
    this.cursorBody = Matter.Bodies.circle(this.width + 100, this.height + 100, 30, {
      render: {
        fillStyle: 'transparent',
        strokeStyle: 'transparent',
        lineWidth: 0,
      },
    });
    this.cursorBody.name = 'cursor';

    // Create ground
    this.groundBody = Matter.Bodies.rectangle(this.width / 2, -100, this.width * 4, 60, {
      isStatic: true,
    });
    this.groundBody.name = 'ground';

    // Add the bodies to the world
    Matter.Composite.add(this.engine.world, [this.cursorBody, this.groundBody]);
  }

  /**
   * BeforeUpdate Matter event
   */
  onBeforeUpdate() {
    if (!this.mouse.position.x) {
      return;
    }

    Matter.Body.setPosition(this.cursorBody, {
      x: this.mouse.position.x,
      y: this.mouse.position.y,
    });
  }

  /**
   * CollisionStart Matter event
   * @param {Event} event Matter event.
   */
  onCollisionStart(event) {
    event.pairs.forEach(this.onCollisionStartPairs); // Anonymous functions in the "requestAnimationFrame" should be kept to a minimum
  }

  /**
   * CollisionStart Matter event loop
   * @param {object} pair Matter event loop.
   * @returns {Bool}
   */
  onCollisionStartPairs(pair) {
    if (pair.bodyA.name === 'ground' && pair.bodyB.name === 'bubble') {
      Matter.World.remove(this.engine.world, pair.bodyB);
      return true;
    }

    if (pair.bodyA.name === 'bubble' && pair.bodyB.name === 'ground') {
      Matter.World.remove(this.engine.world, pair.bodyA);
      return true;
    }

    return false;
  }

  /**
   * Brower Tab change
   */
  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.play();
    } else {
      this.pause();
    }
  }

  /**
   * Add Bubbles Path
   * @param {Integer} interval setInterval value.
   * @param {Integer} delay setInterval delay.
   * @param {Float} x Bubble position x
   * @param {Float} y Bubble position y
   * @param {Integer} size Bubble size
   * @param {object} bubbleStyle Bubble style
   */
  addBubblePath(interval = 1000, delay = 0, x = 0, y = 0, size = 17, bubbleStyle = null) {
    this.bubbles.push({
      interval,
      delay,
      event: null,
      config: {
        x,
        y,
        size,
        bubbleStyle,
      },
    });
  }

  /**
   * Add Bubble Body
   * @param {Float} x Bubble position x.
   * @param {Float} y Bubble position y.
   * @param {Integer} size Bubble size.
   * @param {object} style Bubble style.
   * @returns {Body} Matter body.
   */
  addBubbleBody(x = 0, y = 0, size = 17, style = null) {
    if (!this.isRunning) {
      return null;
    }

    const bubble = Matter.Bodies.rectangle(this.width * x, this.height * y, size * 1.35, size, {
      chamfer: {
        radius: size / 2,
      },
      render: style ?? this.options.bubbleStyle,
    });
    bubble.name = 'bubble';

    Matter.Composite.add(this.engine.world, [bubble]);

    return bubble;
  }

  /**
   * Get random number between
   * @param {Integer} min Min value.
   * @param {Integer} max Max value.
   * @returns {Integer} Random value.
   */
  randomMinMax(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }
}
